import axios from 'axios';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import i18next from 'i18next'; // Use singleton — NOT the local config file
import { store } from 'store';
import { logoff, refreshTokenSucess } from 'store/auth';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { HttpStatus } from 'utils/http_status_utils';

/**
 * Helper function to retrieve auth and refresh tokens from localStorage
 */
const getAuthAndRefreshToken = () => {
  const { auth } = JSON.parse(localStorage.getItem('persist:root'));
  const authData = JSON.parse(auth);

  let token = null;
  let refreshToken = null;
  if (!authData) {
    return { token, refreshToken };
  }
  if (authData?.token) {
    token = authData.token.replace('"', '').replace('"', '');
    token = token !== '' ? token : null;
  }
  if (authData?.refreshToken) {
    refreshToken = authData.refreshToken.replace('"', '').replace('"', '');
    refreshToken = refreshToken !== '' ? refreshToken : null;
  }

  return { token, refreshToken };
};

/**
 * Get API base URL from environment variable
 * Defaults to current hostname without port for multi-tenant support
 *
 * For local development:
 * - Frontend at http://vo.localhost:3000 → API at http://vo.localhost (port 80, Nginx)
 * - Frontend at http://company.localhost:3000 → API at http://company.localhost (port 80, Nginx)
 */
const getBaseURL = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // Dynamically construct base URL from current hostname (tenant-aware)
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol; // http: or https:
    const hostname = window.location.hostname; // vo.localhost, company.localhost, etc.
    return `${protocol}//${hostname}`;
  }

  // Fallback for server-side rendering
  return 'http://localhost';
};

/**
 * apiClientInternal - Internal axios instance with no interceptors
 *
 * Used for:
 * - Token refresh calls (avoids circular dependency)
 *
 * No interceptors to prevent infinite loops during token refresh
 */
export const apiClientInternal = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * apiClient - Authenticated axios instance
 *
 * Replaces: backendService, backendPrivate, backendPolicy, backendMapping
 *
 * Features:
 * - Auto JWT token injection
 * - Auto token refresh when expired
 * - 401 unauthorized handling with session expiry modal
 *
 * Use for: All authenticated API calls to microservices
 */
export const apiClient = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * Request interceptor for apiClient
 * - Injects JWT token from localStorage
 * - Refreshes token if expired before request
 */
apiClient.interceptors.request.use(
  async config => {
    const { token, refreshToken } = getAuthAndRefreshToken();
    if (!config.headers?.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (!token) return config;

    const user = jwtDecode(token);
    const isExpired = dayjs.unix(user.exp).diff(dayjs()) < 1;
    if (!isExpired) return config;

    // Token is expired, refresh it
    const response = await apiClientInternal.post(`/user/auth/refresh-token`, {
      refreshToken,
    });
    if (response.status === 200 && !!response.data) {
      config.headers.Authorization = `Bearer ${response.data.token}`;
      store.dispatch(refreshTokenSucess(response.data));
    }
    return config;
  },
  error => Promise.reject(error),
);

/**
 * Response interceptor for apiClient
 * - Handles 401 unauthorized errors
 * - Shows session expiry modal
 * - Redirects to login page
 */
apiClient.interceptors.response.use(
  response => response,
  async error => {
    if (error.response && error.response.status === HttpStatus.Unauthorized) {
      const swal = withReactContent(Swal);
      const result = await swal.fire({
        title: i18next.t('session.expiredTitle'),
        text: i18next.t('session.expiredMessage'),
        icon: 'warning',
        backdrop: true,
        showCancelButton: false,
        confirmButtonColor: '#DD6B55',
        confirmButtonText: 'OK',
      });

      if (result.isConfirmed) {
        store.dispatch(logoff());
        window.location.replace('/login');
      }

      return Promise.reject('Unauthorized');
    } else {
      return Promise.reject(error);
    }
  },
);

/**
 * Accept-Language interceptor for apiClient
 * Added AFTER the JWT token interceptor — order: token injection → language header → request
 */
apiClient.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = i18next.language || 'en';
  return config;
});

/**
 * apiClientPublic - Public axios instance with no authentication
 *
 * Replaces: backendServicePublic
 *
 * Features:
 * - No authentication headers
 * - No interceptors
 *
 * Use for: Public endpoints (login, registration, public policies, etc.)
 */
export const apiClientPublic = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/**
 * Accept-Language interceptor for apiClientPublic
 * Also adds language header for unauthenticated endpoints (e.g., country config)
 */
apiClientPublic.interceptors.request.use((config) => {
  config.headers['Accept-Language'] = i18next.language || 'en';
  return config;
});
